import { formatResearcherFullName } from 'utils/researcherName';
import { Fragment } from 'react';
import { Checkbox, Link, ResearcherEmail, Tooltip } from 'components/common';
import { format } from 'utils/date';
import { getResearcherText } from 'utils/constants';

import { makeHttpsUrl } from 'utils/helpers/externalLink';
import SocialMediaLink from 'components/Researcher/Profile/SocialMediaLink';
import { colours } from 'utils/theme';
import { formatCurrency, formatNumber } from 'utils/number';
import CRMStatus from './CRMStatus';
import { LockedIcon } from './LockIcon';
import { CenterItem, HeaderIcon, TwoLineLabel } from './styled';

const getConfig = (
  listId,
  user,
  selectedMembers,
  someSelected,
  allSelected,
  handleToggleAll,
  handleToggleMember,
  canVisitOrgPage
) => {
  const config = {
    headers: [
      {
        testId: 'checkbox',
        label: (
          <Checkbox
            checked={allSelected}
            indeterminate={someSelected}
            onClick={handleToggleAll}
          />
        ),
        minWidth: 20,
      },
      {
        label: getResearcherText({ capitalize: true }),
        key: 'name',
        minWidth: 150,
        maxContentWidth: 250,
      },
      {
        label: 'Organisation',
        key: 'organisationname',
        minWidth: 150,
        maxContentWidth: 250,
      },
      { label: 'Email', key: 'email', minWidth: 50, maxContentWidth: 250 },
      {
        label: (
          <CenterItem>
            <Tooltip title='Funding Awards'>
              {' '}
              <HeaderIcon icon='award' color={colours.grants} size='xl' />{' '}
            </Tooltip>
          </CenterItem>
        ),
        minWidth: 30,
      },
      {
        label: (
          <CenterItem>
            <Tooltip title='Tradeshow Sessions'>
              {' '}
              <HeaderIcon icon='image' color={colours.posters} size='xl' />{' '}
            </Tooltip>
          </CenterItem>
        ),
        minWidth: 30,
      },
      {
        label: (
          <CenterItem>
            <Tooltip title='Publications'>
              {' '}
              <HeaderIcon
                icon='book'
                color={colours.publications}
                size='xl'
              />{' '}
            </Tooltip>
          </CenterItem>
        ),
        minWidth: 30,
      },
      {
        label: (
          <CenterItem>
            <Tooltip title='Clinical Trials'>
              {' '}
              <HeaderIcon icon='trial' color={colours.trials} size='xl' />{' '}
            </Tooltip>
          </CenterItem>
        ),
        minWidth: 30,
      },
      {
        label: (
          <TwoLineLabel>
            Funding <div>(Last 3 Years)</div>
          </TwoLineLabel>
        ),
        minWidth: 50,
      },
      {
        label: (
          <Tooltip title="Research Impact (3 Year Avg): Reflects the contact's impact based on the number of citations their work has received and the impact factor of the journals they publish in, similar to the H-index">
            <TwoLineLabel>
              Research Impact <div>(3 Year Avg)</div>
            </TwoLineLabel>
          </Tooltip>
        ),
        minWidth: 50,
      },
      { label: 'Location', key: '', minWidth: 100, maxContentWidth: 180 },
      {
        label: <Tooltip title='LinkedIn and CRM integration'>Links</Tooltip>,
        key: '',
        minWidth: 80,
      },
      { label: 'Added On', key: 'dateAddedUtc', minWidth: 80 },
      { label: 'Added By', key: 'addedbyuser', minWidth: 80 },
    ],
    createRow: (r, i) => (
      <Fragment key={`${r.name}-${i}`}>
        {/* Checkbox */}
        <div>
          <Checkbox
            checked={
              !selectedMembers.exclude[r.researcherId] &&
              (selectedMembers.include[r.researcherId] || selectedMembers.all)
            }
            onClick={() => handleToggleMember(r.researcherId)}
          />
        </div>
        {/* Researcher */}
        <div>
          {!r.profileOnly && (
            <div>
              <Link to={`/researcher/${r.researcherId}`}>
                {formatResearcherFullName(r.name)}
              </Link>
            </div>
          )}
          {r.profileOnly && (
            <div>
              <span>{formatResearcherFullName(r.name)}</span>
            </div>
          )}
        </div>
        {/* Organisation */}
        <div>
          {r.organisationId > 0 && canVisitOrgPage && (
            <div>
              <Link to={`/organisation/${r.organisationId}`}>
                {r.organisationName}
              </Link>
            </div>
          )}
          {(r.organisationId === 0 || !canVisitOrgPage) && (
            <div>
              <span>{r.organisationName}</span>
            </div>
          )}
        </div>
        {/* Email */}
        {!user.hideResearcherEmail && (
          <div>
            {r.locked || r.email === 'TrialUser' ? (
              <LockedIcon />
            ) : (
              <ResearcherEmail
                email={r.email}
                emailStatusCategory={r.emailVerificationCategory}
                hideIcon
              />
            )}
          </div>
        )}
        <CenterItem>
          {r.locked ? <LockedIcon /> : r.totalGrants || 0}
        </CenterItem>
        <CenterItem>
          {r.locked ? <LockedIcon /> : r.totalSessions || 0}
        </CenterItem>
        <CenterItem>
          {r.locked ? <LockedIcon /> : r.totalPublications || 0}
        </CenterItem>
        <CenterItem>
          {r.locked ? <LockedIcon /> : r.totalClinicalTrials || 0}
        </CenterItem>
        <div>
          {r.locked ? (
            <LockedIcon />
          ) : r.threeYearTotalFunding ? (
            `$${formatCurrency(r.threeYearTotalFunding)}`
          ) : (
            ''
          )}
        </div>
        <div>
          {r.locked ? (
            <LockedIcon />
          ) : r.threeYearHIndexAverage ? (
            formatNumber(r.threeYearHIndexAverage, false)
          ) : (
            ''
          )}
        </div>
        {/* State */}
        <div>
          {r.locked ? (
            <LockedIcon />
          ) : r.state && r.country ? (
            `${r.state}, ${r.country}`
          ) : (
            r.country
          )}
        </div>
        {/* Linkedin and CRMStatus */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {r.locked ? (
            <LockedIcon />
          ) : (
            <SocialMediaLink
              network={{
                type: 'linkedin',
                displayName: 'LinkedIn',
              }}
              title={r.linkedIn ? 'View on LinkedIn' : 'Search for LinkedIn'}
              url={
                r.linkedIn
                  ? makeHttpsUrl(r.linkedIn)
                  : `http://www.google.com/search?q=${r.name} ${r.organisationName} site:linkedin.com`
              }
              isMuted={!r.linkedIn}
              logDescription={{
                entityType: 'Researcher',
                location: `Lists:${listId}`,
              }}
            />
          )}
          <CRMStatus crmPushDetails={r.crmPushDetails} />
        </div>
        {/* DateAdded */}
        <div>{format(r.dateAddedUtc)}</div>
        {/* AddedBy */}
        <div>{r.addedByUser || 'Deleted User'}</div>
      </Fragment>
    ),
  };

  if (user.hideResearcherEmail)
    config.headers = config.headers.filter(p => p.key !== 'email');

  return config;
};

export default getConfig;
